/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetProps,
  SheetTitle,
} from '@eigtech/ui/components/sheet'
import { VStack } from '@eigtech/ui/components/stack'
import { TypographyH4 } from '@eigtech/ui/components/typopgraphy'
import { FieldValues, SubmitHandler } from 'react-hook-form'
import { FormApi } from '../useForm'

export type FormDrawerProps<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues,
> = {
  children?: React.ReactNode
  description?: React.ReactNode
  form: FormApi<TFieldValues, TContext, TTransformedValues>
  resetFormOnClose?: boolean
  submitButton?: React.ReactNode
  title: React.ReactNode
  onSubmit: SubmitHandler<TTransformedValues> | undefined
} & SheetProps

export function FormDrawer<
  TFieldValues extends FieldValues = FieldValues,
  TContext = any,
  TTransformedValues = TFieldValues,
>({
  children,
  description,
  form,
  resetFormOnClose = true,
  submitButton,
  title,
  onSubmit,
  ...props
}: FormDrawerProps<TFieldValues, TContext, TTransformedValues>) {
  const {
    formState: { isSubmitting },
    reset,
  } = form

  return (
    <Sheet
      closeDisabled={isSubmitting}
      disablePointerDismissal
      {...props}
      onOpenChange={(open, event) => {
        if (resetFormOnClose && !open) {
          reset()
        }

        props.onOpenChange?.(open, event)
      }}
    >
      <SheetContent>
        <SheetHeader>
          {typeof title === 'string' ? (
            <SheetTitle render={<TypographyH4 />}>{title}</SheetTitle>
          ) : (
            title
          )}
          {!!description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>

        <SheetBody className="px-2">
          {onSubmit ? (
            <VStack
              className="flex-1 gap-6 overflow-auto px-2 py-px"
              data-slot="sheet-form"
              render={<form.Form onSubmit={onSubmit} />}
            >
              {children}
            </VStack>
          ) : (
            children
          )}
        </SheetBody>

        <SheetFooter>
          <SheetClose>Cancel</SheetClose>

          {!submitButton || typeof submitButton === 'string' ? (
            <form.SubmitButton size="md">{submitButton}</form.SubmitButton>
          ) : (
            submitButton
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
